export const IMAGE_MAX_DIMENSION = 2400;
export const IMAGE_MAX_BYTES = 800 * 1024;
export const IMAGE_INITIAL_QUALITY = 0.82;

const MIN_QUALITY = 0.54;
const QUALITY_STEP = 0.07;
const DIMENSION_STEP = 0.85;
const MIN_DIMENSION = 320;

function loadHtmlImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () =>
      resolve({
        source: image,
        width: image.naturalWidth || image.width,
        height: image.naturalHeight || image.height,
        cleanup: () => URL.revokeObjectURL(url),
      });
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Das Bildformat wird nicht unterstützt oder die Datei ist beschädigt."));
    };
    image.src = url;
  });
}

async function decodeImage(file) {
  if (!file || !String(file.type || "").startsWith("image/")) {
    throw new Error("Bitte wähle eine gültige Bilddatei aus.");
  }

  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        cleanup: () => bitmap.close?.(),
      };
    } catch (_error) {
      // Safari und ältere Browser können einzelne Formate nur über HTMLImage dekodieren.
    }
  }

  return loadHtmlImage(file);
}

function encodeJpeg(canvas, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob
          ? resolve(blob)
          : reject(new Error("Das Bild konnte nicht als JPEG gespeichert werden.")),
      "image/jpeg",
      quality
    );
  });
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Das optimierte Bild konnte nicht gelesen werden."));
    reader.readAsDataURL(blob);
  });
}

function fitDimensions(width, height, maxDimension) {
  const scale = Math.min(1, maxDimension / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

export async function processImageFile(file) {
  let decoded;

  try {
    decoded = await decodeImage(file);
    if (!decoded.width || !decoded.height) {
      throw new Error("Das Bild enthält keine gültigen Abmessungen.");
    }

    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Die Bildverarbeitung wird von diesem Gerät nicht unterstützt.");
    }

    let dimensions = fitDimensions(
      decoded.width,
      decoded.height,
      IMAGE_MAX_DIMENSION
    );

    for (let resizeAttempt = 0; resizeAttempt < 12; resizeAttempt += 1) {
      canvas.width = dimensions.width;
      canvas.height = dimensions.height;
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(decoded.source, 0, 0, canvas.width, canvas.height);

      for (
        let quality = IMAGE_INITIAL_QUALITY;
        quality >= MIN_QUALITY - 0.001;
        quality -= QUALITY_STEP
      ) {
        const blob = await encodeJpeg(canvas, Math.max(MIN_QUALITY, quality));
        if (blob.size <= IMAGE_MAX_BYTES) {
          return await blobToDataUrl(blob);
        }
      }

      if (Math.max(dimensions.width, dimensions.height) <= MIN_DIMENSION) {
        break;
      }
      dimensions = fitDimensions(
        dimensions.width,
        dimensions.height,
        Math.max(MIN_DIMENSION, Math.round(Math.max(dimensions.width, dimensions.height) * DIMENSION_STEP))
      );
    }

    throw new Error("Das Bild konnte nicht ausreichend verkleinert werden.");
  } catch (error) {
    if (error instanceof Error && error.message) {
      throw error;
    }
    throw new Error("Das Bild konnte nicht verarbeitet werden. Bitte verwende ein anderes Foto.");
  } finally {
    decoded?.cleanup?.();
  }
}
